"use client";

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { normalizeChinaMobile } from "@/lib/chinaMobile";
import { parseApiError } from "@/lib/parseApiError";

const RESEND_COOLDOWN_SEC = 60;

export type PhoneCodeLoginAccent = "indigo" | "green" | "blue";

const accentStyles: Record<
  PhoneCodeLoginAccent,
  { ring: string; btn: string; link: string }
> = {
  indigo: {
    ring: "focus:ring-indigo-500",
    btn: "bg-indigo-600 hover:bg-indigo-700",
    link: "text-indigo-600",
  },
  green: {
    ring: "focus:ring-green-500",
    btn: "bg-green-600 hover:bg-green-700",
    link: "text-green-600",
  },
  blue: {
    ring: "focus:ring-blue-500",
    btn: "bg-blue-600 hover:bg-blue-700",
    link: "text-blue-600",
  },
};

async function requestVerificationCode(mobile: string): Promise<{ retryAfterSeconds?: number }> {
  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/users/request-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mobile }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    const { message, retryAfterSeconds } = parseApiError(errorData, "验证码发送失败");
    const err = new Error(message) as Error & { retryAfterSeconds?: number };
    err.retryAfterSeconds = retryAfterSeconds;
    throw err;
  }

  return {};
}

export type PhoneCodeLoginFormProps = {
  accent: PhoneCodeLoginAccent;
  onLogin: (mobile: string, code: string) => Promise<void>;
};

export default function PhoneCodeLoginForm({ accent, onLogin }: PhoneCodeLoginFormProps) {
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"mobile" | "code">("mobile");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const styles = accentStyles[accent];

  useLayoutEffect(() => {
    if (step !== "code" || loading) return;
    codeInputRef.current?.focus({ preventScroll: true });
  }, [step, loading]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timer = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const startResendCooldown = useCallback((seconds: number = RESEND_COOLDOWN_SEC) => {
    setResendCooldown(seconds);
  }, []);

  const sendCode = useCallback(
    async (trimmedMobile: string) => {
      try {
        await requestVerificationCode(trimmedMobile);
        setStep("code");
        setMobile(trimmedMobile);
        setCode("");
        startResendCooldown(RESEND_COOLDOWN_SEC);
      } catch (err) {
        const retryAfter =
          err instanceof Error
            ? (err as Error & { retryAfterSeconds?: number }).retryAfterSeconds
            : undefined;
        if (retryAfter !== undefined && retryAfter > 0) {
          if (step === "code") {
            startResendCooldown(retryAfter);
          }
        }
        setError(err instanceof Error ? err.message : "验证码发送失败");
        throw err;
      }
    },
    [startResendCooldown, step],
  );

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedMobile = normalizeChinaMobile(mobile);
    if (!mobile.trim()) {
      setError("请输入手机号");
      return;
    }
    if (!normalizedMobile) {
      setError("请输入正确的中国大陆手机号");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await sendCode(normalizedMobile);
    } catch {
      /* error state set in sendCode */
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || resendLoading || !mobile) {
      return;
    }

    setResendLoading(true);
    setError(null);

    try {
      await sendCode(mobile);
    } catch {
      /* error state set in sendCode */
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onLogin(mobile, code.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMobile = () => {
    setStep("mobile");
    setCode("");
    setResendCooldown(0);
    setError(null);
  };

  const inputClass = `w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${styles.ring}`;

  return (
    <>
      {step === "mobile" ? (
        <form onSubmit={handleSendCode} className="w-full max-w-md">
          <div className="flex flex-col gap-4">
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="输入手机号"
              className={inputClass}
            />
            <button
              type="submit"
              disabled={!mobile.trim() || loading}
              className={`w-full px-6 py-3 text-white rounded-lg disabled:opacity-50 transition-colors ${styles.btn}`}
            >
              {loading ? "正在发送" : "获取验证码"}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleLogin} className="w-full max-w-md">
          <div className="flex flex-col gap-4">
            <input type="text" value={mobile} disabled className={`${inputClass} bg-gray-100`} />
            <div className="flex items-center gap-3">
              <input
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="输入验证码"
                className={`flex-1 min-w-0 p-3 border rounded-lg focus:outline-none focus:ring-2 ${styles.ring}`}
              />
              {resendCooldown > 0 ? (
                <span className={`text-sm whitespace-nowrap shrink-0 ${styles.link}`}>
                  {resendCooldown} 秒后可重新获取
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleResendCode()}
                  disabled={resendLoading}
                  className={`text-sm whitespace-nowrap shrink-0 hover:underline disabled:opacity-50 ${styles.link}`}
                >
                  {resendLoading ? "正在发送" : "重新获取验证码"}
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={!code.trim() || loading}
              className={`w-full px-6 py-3 text-white rounded-lg disabled:opacity-50 transition-colors ${styles.btn}`}
            >
              {loading ? "正在登录" : "登录"}
            </button>
            <button
              type="button"
              onClick={handleChangeMobile}
              className={`w-full hover:underline text-center ${styles.link}`}
            >
              更换手机号
            </button>
          </div>
        </form>
      )}
      {error && <p className="text-red-500 mt-4 text-center max-w-md">{error}</p>}
    </>
  );
}
